import { getMemberBirthCalendarParts } from "./family-tree/dateAndTime";

/**
 * The milestone ages the report groups by — the same list the API groups the
 * current year's members into: 1, 5, 7, 10, 15, 16, 18, 20, 21, 25, every
 * multiple of 5 up to 120, and 62.
 */
export const DASHBOARD_MILESTONE_AGES = (() => {
  const ages = new Set([1, 5, 7, 10, 15, 16, 18, 20, 21, 25, 62]);
  for (let age = 5; age <= 120; age += 5) ages.add(age);
  return [...ages].sort((a, b) => a - b);
})();

const MILESTONE_AGE_SET = new Set(DASHBOARD_MILESTONE_AGES);

/** How many years the year picker runs past the current one. */
export const DASHBOARD_MILESTONE_YEAR_SPAN = 5;

/** This year first, then the next few — never a past year, as on the other
 *  dashboard year pickers. A year restored from elsewhere still shows. */
export function getDashboardMilestoneYearOptions(selectedYear) {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y <= current + DASHBOARD_MILESTONE_YEAR_SPAN; y++) {
    years.push(y);
  }
  if (selectedYear != null && !years.includes(selectedYear)) {
    years.push(selectedYear);
  }
  return years
    .sort((a, b) => a - b)
    .map((y) => ({ value: y, label: String(y) }));
}

export function isDashboardMilestoneAge(age) {
  return MILESTONE_AGE_SET.has(age);
}

/** The age a member turns during `year` — null without a usable date of birth. */
export function getDashboardAgeInYear(member, year) {
  const birthYear = getMemberBirthCalendarParts(member)?.year;
  if (!Number.isFinite(birthYear)) return null;
  const age = year - birthYear;
  return age < 0 ? null : age;
}

/**
 * The milestone groups for a year the API cannot be asked about.
 *
 * `/dashboard-statistics-with-details` takes only a family_tree_id and answers
 * for the current year, so any other year is bucketed here instead, off every
 * member's date of birth: whoever turns a milestone age during `year` lands in
 * that age's group. The shape matches what the API returns —
 * `{ milestone, count, details }`, oldest milestone first — so the page renders
 * a computed year exactly as it renders the API's own.
 *
 * `preferredRows` is the current year's API rows, keyed by member id: where a
 * member appears in both, the API's row wins, since it carries whatever extra
 * the plain member list does not. A member with no date of birth cannot be
 * placed in any year and is left out.
 */
export function buildAgeMilestoneGroupsForYear(members, year, preferredRows) {
  const byId = new Map();
  (preferredRows || []).forEach((row) => {
    const id = row?.id ?? row?.member_id;
    if (id != null && id !== "") byId.set(String(id), row);
  });

  const byMilestone = new Map();

  (Array.isArray(members) ? members : []).forEach((member) => {
    const age = getDashboardAgeInYear(member, year);
    if (age == null || !isDashboardMilestoneAge(age)) return;

    const id = member?.id ?? member?.member_id;
    const row = (id != null && byId.get(String(id))) || member;

    if (!byMilestone.has(age)) byMilestone.set(age, []);
    byMilestone.get(age).push(row);
  });

  return [...byMilestone.entries()]
    .map(([milestone, details]) => ({ milestone, count: details.length, details }))
    .sort((a, b) => b.milestone - a.milestone);
}
